import { Component } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';

export interface CompareDialogData {
  compareColumnHeader: string;
  depthModeOptions: { label: string; value: string }[];
  depthMode: string;
  gridData: any[];
  compareColumnField: string;
  mdField: string;
  tvdField: string;
  mdHeader: string;
  tvdHeader: string;
}

@Component({
  selector: 'app-compare-dialog',
  template: `
    <div class="flex gap-4 h-full overflow-hidden" style="height: 495px;">
      <div class="flex flex-col gap-2 w-[300px] shrink-0 h-full">
        @if (depthModeOptions.length > 1) {
          <div class="shrink-0">
            <p-selectButton [options]="depthModeOptions" [(ngModel)]="depthMode" (onChange)="onDepthToggleChange()" size="small"></p-selectButton>
          </div>
        }
        <div class="h-1/2 overflow-auto">
          <p-table [value]="comparePreviewData" [scrollable]="true" scrollHeight="flex" class="text-sm">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ depthColumnHeader }}</th>
                <th>{{ compareColumnHeader }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="pr-4">{{ row.depth }}</td>
                <td>{{ row.value }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <div class="h-1/2 flex flex-col gap-2">
          <label class="text-sm">Paste two columns ({{ depthColumnHeader }} and value), tab or comma separated:</label>
          <textarea
            pInputTextarea
            [(ngModel)]="comparePasteText"
            rows="4"
            class="w-full flex-1 font-mono text-sm"
            placeholder="e.g.&#10;1000&#9;50.5&#10;2000&#9;75.3&#10;3000&#9;100.1"
          ></textarea>
          <div class="flex gap-2 pt-2">
            <p-button label="Clear" severity="danger" outlined="true" icon="pi pi-times" (onClick)="clearCompareData()"></p-button>
            <p-button label="Apply" severity="success" outlined="true" icon="pi pi-check" (onClick)="applyComparePastedData()" [disabled]="!comparePasteText"></p-button>
          </div>
          @if (comparePasteError) {
            <small class="text-red-400">{{ comparePasteError }}</small>
          }
        </div>
      </div>

      <div class="flex-1 min-w-0 overflow-hidden">
        <app-xy-line-plot
          class="h-[500px]"
          [plotData]="comparePlotData"
          [xAxisTitle]="compareColumnHeader"
          [yAxisTitle]="depthColumnHeader"
          plotName="compareValuesGridPlot"
        ></app-xy-line-plot>
      </div>
    </div>
  `,
  standalone: false
})
export class CompareDialogComponent {
  public compareColumnHeader: string;
  public depthColumnHeader = '';
  public depthModeOptions: { label: string; value: string }[];
  public depthMode: string;
  public comparePreviewData: { depth: number; value: number }[] = [];
  public compareUserData: { depth: number; value: number }[] = [];
  public comparePasteText = '';
  public comparePasteError = '';
  public comparePlotData: any[] = [];

  private readonly gridData: any[];
  private readonly compareColumnField: string;
  private readonly mdField: string;
  private readonly tvdField: string;
  private readonly mdHeader: string;
  private readonly tvdHeader: string;

  constructor(public config: DynamicDialogConfig) {
    const data: CompareDialogData = config.data;
    this.compareColumnHeader = data.compareColumnHeader;
    this.depthModeOptions = data.depthModeOptions;
    this.depthMode = data.depthMode;
    this.gridData = data.gridData;
    this.compareColumnField = data.compareColumnField;
    this.mdField = data.mdField;
    this.tvdField = data.tvdField;
    this.mdHeader = data.mdHeader;
    this.tvdHeader = data.tvdHeader;
    this.updateDepthSource();
    this.updateComparePlot();
  }

  onDepthToggleChange(): void {
    this.updateDepthSource();
    this.updateComparePlot();
  }

  applyComparePastedData(): void {
    this.comparePasteError = '';
    const lines = this.comparePasteText.trim().split(/\r?\n/).filter(l => l.trim());
    const parsed: { depth: number; value: number }[] = [];

    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(/[\t,;]+/).map(s => s.trim()).filter(s => s);
      if (parts.length < 2) {
        this.comparePasteError = `Row ${i + 1}: expected 2 columns, got ${parts.length}`;
        return;
      }
      const depth = parseFloat(parts[0]);
      const value = parseFloat(parts[1]);
      if (isNaN(depth) || isNaN(value)) {
        this.comparePasteError = `Row ${i + 1}: non-numeric value found`;
        return;
      }
      parsed.push({ depth, value });
    }

    if (parsed.length === 0) {
      this.comparePasteError = 'No valid data found';
      return;
    }

    this.compareUserData = parsed;
    this.updateComparePlot();
  }

  clearCompareData(): void {
    this.compareUserData = [];
    this.comparePasteText = '';
    this.comparePasteError = '';
    this.updateComparePlot();
  }

  private updateDepthSource(): void {
    const isTvd = this.depthMode === 'tvd';
    const depthField = isTvd ? (this.tvdField || this.mdField) : (this.mdField || this.tvdField);
    const depthHeader = isTvd ? (this.tvdHeader || this.mdHeader) : (this.mdHeader || this.tvdHeader);
    this.depthColumnHeader = depthHeader;
    this.comparePreviewData = this.gridData.map(r => ({
      depth: r[depthField],
      value: r[this.compareColumnField]
    }));
  }

  private updateComparePlot(): void {
    const isTvd = this.depthMode === 'tvd';
    const depthField = isTvd ? (this.tvdField || this.mdField) : (this.mdField || this.tvdField);

    const tableTrace: any = {
      x: this.gridData.map(r => r[this.compareColumnField]),
      y: this.gridData.map(r => r[depthField]),
      mode: 'lines',
      name: this.compareColumnHeader,
      line: { color: '#42A5F5' }
    };

    const traces: any[] = [tableTrace];

    if (this.compareUserData.length > 0) {
      const sorted = [...this.compareUserData].sort((a, b) => a.depth - b.depth);
      traces.push({
        x: sorted.map(d => d.value),
        y: sorted.map(d => d.depth),
        mode: 'lines',
        name: 'User Data',
        line: { color: '#FF7043', dash: 'dash' }
      });
    }

    this.comparePlotData = traces;
  }
}
